import {diff} from 'deep-object-diff';
import UserController from '../controllers/UserController';

export const createCopyChangeLog = (sourcePRId, copiedPRId) => {
    const changeLog = [];

    changeLog.push({
        oldValue: `Copied from EPR-${sourcePRId}`,
        newValue: `EPR-${copiedPRId}`,
        source: '',
        field: '',
    });
    return changeLog;
};

export const createSubmissionChangeLog = (sourcePRId) => {
    const changeLog = [];

    const currentUser = UserController.getCurrentUserName('EZPR-USER');
    const currentUserName = localStorage.getItem('userFullName');
    changeLog.push({
        oldValue: '',
        newValue: `EPR-${sourcePRId} submitted by ${currentUserName}(${currentUser}).`,
        source: 'Submitted to SAP',
        field: '',
    });
    return changeLog;
};

export const createChangeLog = (originalPr, currentPr, submitted = false) => {
    const changedDiff = diff(originalPr, currentPr);

    const getEntries = (obj, prefix = '') => {
        return Object.entries(obj).flatMap(([k, v]) => {
            // if nested values is an object and it is not an array
            if (Object(v) === v && !Array.isArray(v)) {
                const obj = Object.assign({}, v);
                if (obj.hasOwnProperty('label')) {
                    return [[`${prefix}${k}.label`, obj['label']]];
                } else {
                    return getEntries(v, `${prefix}${k}.`);
                }
            } else if (Array.isArray(v)) {
                return getEntries(v, `${prefix}${k}.`);
            } else {
                return [[`${prefix}${k}`, v]];
            }
        });
    };

    const keyValues = getEntries(changedDiff);

    const updatedChanges = keyValues.map(([key, value]) => {
        const keyArray = key.split('.');
        const oldValue = _.get(originalPr, key);
        const isRemoved = Object(oldValue) === oldValue && value === undefined;
        const isCreated = Object(value) === value && oldValue === undefined;
        // Filter out these from log.
        if (keyArray.some((key) =>
            key === 'id' ||
            key === 'material_id' ||
            key === 'transportCosts' ||
            key === 'arePriceDataFromST' ||
            key === 'editableSupplier' ||
            key === 'defaultDeliveryAddress' ||
            key === 'deliveryAddress')) {
            return;
        }
        if (oldValue !== value) {
            return {
                oldValue: handleValues(oldValue),
                newValue: isRemoved ? 'Removed' : isCreated ? 'Created' : handleValues(value),
                source: transformSource(key),
                field: findField(key),
            };
        }
    }).filter((val) => val).filter((val) => val.oldValue !== val.newValue);

    // To handle delivery address, we should handle each property inside delivery address object.
    keyValues.forEach(([key, value]) => {
        // If headerDetails delivery address changed.
        if (key.includes('defaultDeliveryAddress')) {
            if (updatedChanges.some((obj) => obj.field === 'Delivery address')) {
                return;
            }
            const oldDeliveryAddress = _.get(originalPr, 'headerDetails.defaultDeliveryAddress');
            const newDeliveryAddress = _.get(currentPr, 'headerDetails.defaultDeliveryAddress');
            updatedChanges.push({
                oldValue: deliveryAddressAsString(oldDeliveryAddress),
                newValue: deliveryAddressAsString(newDeliveryAddress),
                source: 'General details',
                field: 'Delivery address',
            });
        }
        // If material delivery address changed.
        if (key.includes('deliveryAddress')) {
            const keyArray = key.split('.');
            if (updatedChanges.some((obj) => obj.field === `Material-${Number(keyArray[1]) + 1} delivery address`)) {
                return;
            }
            const oldDeliveryAddress = _.get(originalPr, `materials.${keyArray[1]}.deliveryAddress`);
            const newDeliveryAddress = _.get(currentPr, `materials.${keyArray[1]}.deliveryAddress`);
            updatedChanges.push({
                oldValue: oldDeliveryAddress ? deliveryAddressAsString(oldDeliveryAddress) : '',
                newValue: deliveryAddressAsString(newDeliveryAddress),
                source: 'Item details',
                field: `Material-${Number(keyArray[1]) + 1} delivery address`,
            });
        }
    });
    if (submitted) {
        const currentUser = UserController.getCurrentUserName('EZPR-USER');
        const currentUserName = localStorage.getItem('userFullName');
        const EPRId = _.get(currentPr, 'EPRId');
        updatedChanges.push({
            oldValue: '',
            newValue: `EPR-${EPRId} submitted by ${currentUserName}(${currentUser}).`,
            source: 'Submitted to SB',
            field: '',
        });
    }
    return updatedChanges;
};

// @todo move to helpers file?
const deliveryAddressAsString = (deliveryAddress) => {
    const nonEmptyParts = [];
    for (const key of ['name', 'name2', 'houseNumber', 'street', 'postalCode', 'city', 'state', 'country']) {
        if (deliveryAddress[key]) {
            nonEmptyParts.push(deliveryAddress[key].trim());
        }
    }
    return nonEmptyParts.join(', ');
};

export const findField = (key) => {
    const keyArray = key.split('.').filter((val) => val !== 'label');
    let field = '';

    if (key === 'sbValidation.denialInfo.denialReason') {
        return 'SB Rejection';
    } else if (key === 'sbValidation.sbApprover') {
        return 'SB Approver';
    } else if (key === 'nbsValidation.obApprover') {
        return 'OB Approver';
    }

    if (keyArray.length === 1) {
        field = transformField(keyArray[0]);
    } else if (keyArray.length === 2) {
        field = transformField(keyArray[1]);
    } else if (key.includes('supplierAddresses')) {
        field = `Supplier address ${keyArray[keyArray.length - 1]}`;
    } else if (key.includes('headerDetails') && keyArray.length > 2) {
        field = `${transformField(keyArray[1])} ${transformField(keyArray[2])}`;
    } else if (key.includes('financialInfo.accounts') && keyArray.length > 3) {
        field = `Account-${Number(keyArray[keyArray.length - 2]) + 1} ${transformField(keyArray[keyArray.length - 1])}`;
    } else if (key.includes('financialInfo.accounts') && keyArray.length <= 3) {
        field = `Account-${Number(keyArray[keyArray.length - 1]) + 1}`;
    } else if (key.includes('financialInfo.accountDetailsByMaterial') && keyArray.length > 4) {
        field = `Account-${Number(keyArray[2]) + 1}-${Number(keyArray[keyArray.length - 2]) + 1} ${transformField(keyArray[keyArray.length - 1])}`;
    } else if (key.includes('financialInfo.accountDetailsByMaterial') && keyArray.length <= 4) {
        field = `Account-${Number(keyArray[keyArray.length - 2]) + 1}`;
    } else if (keyArray[0] === 'materials' && keyArray.length) {
        field = `Material-${Number(keyArray[1]) + 1} ${keyArray.length > 3 ? `${transformField(keyArray[2])} ${transformField(keyArray[3])}` : transformField(keyArray[2])}`;
    } else if (key.includes('mainAttachments')) {
        field = `Attachment-${Number(keyArray[2]) + 1} ${keyArray[3]}`;
    } else if (key.includes('competitiveBiddingJustificationAttachments')) {
        field = `CB Attachment-${Number(keyArray[2]) + 1} ${transformField(keyArray[3])}`;
    }

    return field;
};

export const transformField = (str) => {
    switch (str?.toLowerCase()) {
    case 'projecttitle':
        return 'Title';
    case 'wayofbuying':
        return 'Way of buying';
    case 'spender':
        return 'Spender';
    case 'requester':
        return 'Requester';
    case 'companycode':
        return 'Company code';
    case 'purchaseorganization':
        return 'Purchase organization';
    case 'purchasinggroup':
        return 'Purchasing group';
    case 'defaultplant':
        return 'Plant';
    case 'defaultstoragelocation':
        return 'Storage location';
    case 'defaultdeliveryaddress':
        return 'Delivery address';
    case 'paymentterms':
        return 'Payment terms';
    case 'newpaymentterms':
        return 'New payment terms';
    case 'orderingaddress':
        return 'Ordering address';
    case 'invoicepartner':
        return 'Invoice partner';
    case 'plant':
        return 'Plant';
    case 'storagelocation':
        return 'Storage location';
    case 'deliveryaddress':
        return 'Delivery address';
    case 'housenumber':
        return 'House number';
    case 'street':
        return 'Street';
    case 'postalcode':
        return 'Postal code';
    case 'city':
        return 'City';
    case 'state':
        return 'State';
    case 'country':
        return 'Country';
    case 'defaultrequesteddeliverydate':
        return 'Requested delivery date';
    case 'requesteddeliverydate':
        return 'Requested delivery date';
    case 'l1category':
        return 'L1 Category';
    case 'l3category':
        return 'L3 Category';
    case 'validitystart':
        return 'Validity start';
    case 'validityend':
        return 'Validity end';
    case 'quantity':
        return 'Quantity';
    case 'itemcategory':
        return 'Item category';
    case 'glnumber':
        return 'GL Number';
    case 'wbs':
        return 'WBS';
    case 'allocatefor':
        return 'Allocate for';
    case 'allocationtype':
        return 'Allocation type';
    case 'accountstatus':
        return 'Account status';
    case 'consumptionlevel':
        return 'Consumption level';
    case 'internalcomment':
        return 'Internal comment';
    case 'externalcomment':
        return 'External comment';
    case 'other':
        return 'Other';
    case 'otheraddress':
        return 'Other address';
    case 'supplier':
        return 'Supplier';
    case 'p2psupplier':
        return 'P2P supplier';
    case 'pslsupplier':
        return 'PSL supplier';
    case 'hideprice':
        return 'Hide price';
    case 'id':
        return 'ID';
    case 'description':
        return 'Description';
    case 'unit':
        return 'Unit';
    case 'price':
        return 'Price';
    case 'currency':
        return 'Currency';
    case 'per':
        return 'Per';
    case 'taxcode':
        return 'Tax code';
    case 'tax':
        return 'Tax';
    case 'total':
        return 'Total';
    case 'freeitem':
        return 'Free item';
    case 'transportcosts':
        return 'Transport costs';
    case 'supplierpartid':
        return 'Supplier part ID';
    case 'arepricedatafromst':
        return 'Price data from ST';
    case 'suppliercomments':
        return 'Supplier comments';
    case 'internalcomments':
        return 'Internal comments';
    case 'accounttype':
        return 'Account type';
    case 'costcenter':
        return 'Cost center';
    case 'profitcenter':
        return 'Profit center';
    case 'file':
        return 'File';
    case 'topic':
        return 'Topic';
    case 'user':
        return 'User';
    case 'date':
        return 'Date';
    case 'name':
        return 'Name';
    case 'name2':
        return 'Name 2';
    case 'alternateAddress':
        return 'Alternate Delivery Address';
    case 'exception':
        return 'Exception';
    case 'selectedexception':
        return 'Selected exception';
    case 'areaccountsplitbymaterial':
        return 'Different payment accounts for each item';
    case 'grbasedinvoiceverification':
        return 'GR Based invoice verification';
    case 'ers':
        return 'ERS';
    case 'goodsreceipt':
        return 'Goods receipt';
    case 'supplierleadtime':
        return 'Supplier lead time';
    case 'rejectionmsg':
        return 'Requester Rejection';
    default:
        return 'Unknown';
    }
};

export const transformSource = (str) => {
    if (str === 'sbValidation.denialInfo.denialReason') {
        return 'Reason';
    } else if (str === 'sbValidation.sbApprover' || str === 'nbsValidation.obApprover') {
        return 'General details';
    }

    const keyArray = str.split('.');
    switch (keyArray[0].toLowerCase()) {
    case 'projecttitle':
        return 'General details';
    case 'wayofbuying':
        return 'General details';
    case 'spender':
        return 'General details';
    case 'requester':
        return 'General details';
    case 'internalcomment':
        return 'Internal comment';
    case 'externalcomment':
        return 'Sending options';
    case 'headerdetails':
        return 'General details';
    case 'sendingoptions':
        return 'Sending options';
    case 'financialinfo':
        return 'Account details';
    case 'materials':
        return 'Item details';
    case 'attachmentsinfo':
        return 'Attachments';
    case 'supplierleadtime':
        return 'Supplier';
    case 'rejectionmsg':
        return 'Reason';
    default:
        return 'Unknown';
    }
};

export const handleValues = (value) => {
    if (Object(value) === value || !value ) {
        return '';
    } else if (value === 0) {
        return '0';
    } else {
        return value.toString();
    }
};
